import React, { useState, useEffect, useRef } from "react";

import {
  Mail,
  Eye,
  Search,
  Calendar,
  Clock,
  XCircle,
  Loader,
  Send,
} from "lucide-react";
import "./SentEmailTable.css";

export default function SentEmailsTable() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const emailContentRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const apiAddress = process.env.REACT_APP_SECRET;

  useEffect(() => {
    if (!emailContentRef.current) return;

    const images = emailContentRef.current.querySelectorAll("img");

    images.forEach((img) => {
      img.style.cursor = "pointer";

      img.onclick = () => {
        let src = img.getAttribute("src");

        if (!src) return;

        // if relative path → convert to full API URL
        if (!src.startsWith("http")) {
          src = `${apiAddress}/${src.replace(/^\/+/, "")}`;
        }

        window.open(src, "_blank");
      };
    });
  }, [selectedEmail]);

  useEffect(() => {
    fetchEmails();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const restaurantData = JSON.parse(
        localStorage.getItem("restaurant") || "{}",
      );
      const restaurantId = restaurantData?.id;

      const res = await fetch(
        `${apiAddress}/api/SubSection/GetEmailByRestaurantId/${restaurantId}`,
      );
      const data = await res.json();
      setEmails(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch single email for preview
  const fetchEmailById = async (emailId) => {
    try {
      setShowModal(true);
      setLoadingEmail(true);
      setSelectedEmail(null);

      const res = await fetch(
        `${apiAddress}/api/SubSection/GetEmailById/${emailId}`,
      );
      const data = await res.json();
      setSelectedEmail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEmail(false);
    }
  };

  // 🔍 Filter
  const filteredEmails = emails.filter(
    (e) =>
      e.sub?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.body?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // 📄 Pagination
  const totalPages = Math.ceil(filteredEmails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmails = filteredEmails.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getStatusBadge = (status) =>
    status === 1 ? (
      <span className="badge pending">
        <Clock size={14} /> Pending
      </span>
    ) : (
      <span className="badge sent">
        <Send size={14} /> Sent
      </span>
    );

  if (loading) {
    return (
      <div className="loader-screen">
        <Loader className="spinner" size={48} />
        <p>Loading emails...</p>
      </div>
    );
  }

  return (
    <div className="email-page">
      {/* Header */}
      <div className="page-header">
        <div className="title">
          <Mail size={32} />
          <div>
            <h1>Sent Emails</h1>
            <p>All emails sent from your restaurant</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="search-container">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by subject or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="refresh-btn" onClick={fetchEmails}>
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Preview</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {filteredEmails.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty">
                  No emails found
                </td>
              </tr>
            ) : (
              paginatedEmails.map((email) => (
                <tr key={email.id}>
                  <td className="subject">{email.sub || "No Subject"}</td>

                  <td
                    className="preview clickable"
                    onClick={() => fetchEmailById(email.id)}
                  >
                    {email.body?.replace(/<[^>]*>/g, "").slice(0, 90)}
                    ...
                  </td>

                  <td>{getStatusBadge(email.statuss)}</td>

                  <td className="date">
                    <Calendar size={14} />
                    {new Date(email.creationDate).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={currentPage === i + 1 ? "active" : ""}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="sent_email_table__modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="sent_email_table__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sent_email_table__modal-header">
              <h2>{selectedEmail?.sub || "Email Preview"}</h2>
              <XCircle onClick={() => setShowModal(false)} />
            </div>

            <div className="sent_email_table__modal-body">
              {loadingEmail ? (
                <div className="sent_email_table__modal-loader">
                  <Loader className="spinner" size={40} />
                  <p>Loading email...</p>
                </div>
              ) : (
                selectedEmail && (
                  <>
                    <div className="sent_email_table__modal-meta">
                      {getStatusBadge(selectedEmail.statuss)}
                      <span>
                        {new Date(selectedEmail.creationDate).toLocaleString()}
                      </span>
                    </div>

                    {selectedEmail.sub && (
                      <h3 className="sent_email_table__modal-subject">
                        {selectedEmail.sub}
                      </h3>
                    )}

                    <div
                      ref={emailContentRef}
                      className="email-content"
                      dangerouslySetInnerHTML={{
                        __html: selectedEmail.body,
                      }}
                    />
                  </>
                )
              )}
            </div>

            <div className="sent_email_table__modal-footer">
              <button onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
